import * as R from '@radix-ui/react-radio-group';
import type { CSSProperties, ReactNode } from 'react';

import './ui.css';

/** `data-*` attributes, passed through to the element. */
type DataAttributes = { [key: `data-${string}`]: string | number | boolean | undefined };

/**
 * One choice from a set (the collection builder's choices, Today's level formes, any
 * radiogroup): Radix RadioGroup, one Tab stop, the arrows moving the choice. Name it with
 * `label` or `labelledBy`. The items print as toggle chips (ink with a tick when chosen) unless
 * given their own class; for the pill-row look, use `Segmented`. `className` replaces the
 * group's wrapping row (`.ui-radios`); `style` and `data-*` pass through to it.
 */
export function RadioGroup<T extends string>({
	value,
	onValueChange,
	label,
	labelledBy,
	describedBy,
	className,
	style,
	children,
	testId,
	...data
}: {
	value: T;
	onValueChange: (value: T) => void;
	label?: string | undefined;
	labelledBy?: string | undefined;
	describedBy?: string | undefined;
	className?: string | undefined;
	style?: CSSProperties | undefined;
	children: ReactNode;
	testId?: string | undefined;
} & DataAttributes) {
	return (
		<R.Root
			{...data}
			className={className ?? 'ui-radios'}
			style={style}
			value={value}
			onValueChange={(v) => onValueChange(v as T)}
			loop
			aria-label={label}
			aria-labelledby={labelledBy}
			aria-describedby={describedBy}
			data-testid={testId}
		>
			{children}
		</R.Root>
	);
}

/**
 * One choice. `className` replaces `chip toggle`; `tick={false}` drops the tick (for an item
 * that shows it's chosen its own way, as the formes do). `style` and `data-*` pass through.
 */
export function RadioGroupItem({
	value,
	disabled,
	children,
	className = 'chip toggle',
	tick = true,
	style,
	testId,
	...data
}: {
	value: string;
	disabled?: boolean | undefined;
	children: ReactNode;
	className?: string | undefined;
	tick?: boolean | undefined;
	style?: CSSProperties | undefined;
	testId?: string | undefined;
} & DataAttributes) {
	return (
		<R.Item {...data} className={tick ? `${className} ui-check` : className} value={value} disabled={disabled} style={style} data-testid={testId}>
			{children}
		</R.Item>
	);
}
